import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, defineMessages } from 'react-intl';
import styled from 'styled-components';

import {
    Colours,
    Sizes,
    Typography,
    Paragraph,
    IconCheckmark,
    IconX,
} from '@ratehub/base-ui';


function QuoteDetails({ coverageDetails, endorsementDetails, ...otherProps }) {
    const intl = useIntl();

    return (
        <QuoteDetails.FlexContainer {...otherProps}>
            <QuoteDetails.Table>
                <QuoteDetails.Heading size="medium">
                    {intl.formatMessage(Messages.COVERAGE)}
                </QuoteDetails.Heading>
                <QuoteDetails.List>
                    <For
                        each="item"
                        of={coverageDetails}
                    >
                        <QuoteDetails.ListItem key={item.label}>
                            <QuoteDetails.Label size="medium">
                                {item.label}
                            </QuoteDetails.Label>
                            <QuoteDetails.Value size="medium">
                                {item.value}
                            </QuoteDetails.Value>
                        </QuoteDetails.ListItem>
                    </For>
                </QuoteDetails.List>
            </QuoteDetails.Table>

            <QuoteDetails.Table>
                <QuoteDetails.Heading size="medium">
                    {intl.formatMessage(Messages.ENDORSEMENTS)}
                </QuoteDetails.Heading>
                <QuoteDetails.List>
                    <For
                        each="item"
                        of={endorsementDetails}
                    >
                        <QuoteDetails.IconListItem key={item.label}>
                            <QuoteDetails.IconContainer>
                                {/* May be able to simplify this once updating to SC v4 */}
                                <Choose>
                                    <When condition={!item.value}>
                                        <IconX
                                            outlineWidth="4px"
                                            strokeWidth="4px"
                                            stroke={Colours.STRAWBERRY}
                                        />
                                    </When>
                                    <Otherwise>
                                        <IconCheckmark
                                            outlineWidth="4px"
                                            strokeWidth="4px"
                                            stroke={Colours.LIME}
                                        />
                                    </Otherwise>
                                </Choose>
                            </QuoteDetails.IconContainer>
                            <QuoteDetails.Label size="medium">
                                {item.label}
                            </QuoteDetails.Label>
                        </QuoteDetails.IconListItem>
                    </For>
                </QuoteDetails.List>
            </QuoteDetails.Table>
        </QuoteDetails.FlexContainer>
    );
}

QuoteDetails.propTypes = {
    coverageDetails: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string,
            label: PropTypes.string,
        }),
    ).isRequired,
    endorsementDetails: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.bool,
            label: PropTypes.string,
        }),
    ).isRequired,
};

const Messages = defineMessages({
    COVERAGE: {
        id: 'Accounts.InsuranceDetails.coverageInformation',
        defaultMessage: 'Coverage information:',
    },
    ENDORSEMENTS: {
        id: 'Accounts.InsuranceDetails.endorsements',
        defaultMessage: 'Endorsements:',
    },
});


QuoteDetails.FlexContainer = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-right: -${Sizes.SPACING.FOUR}; 
    margin-bottom: ${Sizes.SPACING.TWO};
`;

QuoteDetails.Table = styled.div`
    flex: 250px 1 0;
    margin-right: ${Sizes.SPACING.FOUR};
`;

QuoteDetails.Heading = styled(Paragraph)`
    color: ${Colours.STONE_DARKEST};
    font-weight: ${Typography.WEIGHTS.MEDIUM};
`;

QuoteDetails.List = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
`;

QuoteDetails.ListItem = styled.li`
    display: flex;
    justify-content: space-between;
`;

QuoteDetails.IconListItem = styled.li`
    display: flex;
`;

QuoteDetails.IconContainer = styled.div`
    /* Update this to use icon sizes once available */
    flex: ${Sizes.SPACING.ONE} 0 0;
    align-self: flex-start;
    margin: 4px ${Sizes.SPACING.THREE_QUARTERS} 0 0;
`;

QuoteDetails.Label = styled(Paragraph)`
    margin: 0;
    color: ${Colours.STONE_DARKEST};
`;

QuoteDetails.Value = styled(Paragraph)`
    margin: 0 0 0 ${Sizes.SPACING.ONE};
    color: ${Colours.STONE_DARKEST};
    font-weight: ${Typography.WEIGHTS.MEDIUM};
`;

export default QuoteDetails;
